<template>
	<div id="wrapper">
		<SidebarView
			:memacc="true"
			:memaccVisible="true"
			:commissionSetup="true"
		/>
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
							<h6 class="m-0 fw-bold text-green">Commission Setup</h6>
							<b-button
								class="akkurate-green-btn btn-size text-white"
								v-b-tooltip.hover="{ variant: 'info' }"
								title="Create a new commission deduction setup"
								aria-label="Add New"
								@click="openCreateModal"
							>
								<font-awesome-icon class="me-1" :icon="['fas', 'plus']" aria-hidden="true" />
								Add New
							</b-button>
						</div>
						<div class="card-body">
							<p class="small text-muted mb-3">
								<strong>Setup</strong> defines rules and GL behaviour only — no member balances are debited here.
								Use <strong>Run</strong> to execute a period, or <strong>Pay commission (Excel)</strong> for file-based payment.
								<strong>Tiered (sum of credits)</strong> uses completed Deposit totals on eligibility products in the run period (see form for bracket fees).
							</p>
							<div class="row g-2 mb-3 align-items-end">
								<div class="col-md-4">
									<label class="akkurate-dark small">Search</label>
									<b-form-input
										v-model="filters.filter"
										placeholder="Name or code..."
										class="field-container"
										@keyup.enter="loadSetups"
									/>
								</div>
								<div class="col-md-3">
									<label class="akkurate-dark small">Status</label>
									<b-form-select
										v-model="filters.is_active"
										:options="activeOptions"
										class="field-container"
										@change="loadSetups"
									/>
								</div>
								<div class="col-md-auto">
									<b-button
										class="akkurate-green-btn btn-size text-white"
										v-b-tooltip.hover="{ variant: 'info' }"
										title="Apply filters and reload the setup list"
										aria-label="Apply filters"
										@click="loadSetups"
									>
										<font-awesome-icon class="me-1" :icon="['fas', 'search']" aria-hidden="true" />
										Apply
									</b-button>
								</div>
							</div>

							<b-table
								striped
								bordered
								responsive
								:busy="isBusy"
								:items="rows"
								:fields="tableFields"
								show-empty
								class="table_text_size"
							>
								<template #table-busy>
									<div class="text-center text-danger my-2">
										<b-spinner class="align-middle" />
										<strong>Loading...</strong>
									</div>
								</template>
								<template #cell(is_active)="data">
									<span
										:class="
											data.item.is_active
												? 'badge akkurate-green-bg'
												: 'badge akkurate-gray'
										"
									>
										{{ data.item.is_active ? "Active" : "Inactive" }}
									</span>
								</template>
								<template #cell(commission_method)="data">
									{{ formatMethod(data.item.commission_method) }}
								</template>
								<template #cell(actions)="data">
									<div class="d-flex flex-wrap gap-1">
										<b-button
											class="btn-size"
											variant="outline-secondary"
											v-b-tooltip.hover="{ variant: 'info' }"
											title="Edit this commission setup"
											aria-label="Edit setup"
											@click="openEditModal(data.item)"
										>
											<font-awesome-icon class="me-1" :icon="['fas', 'edit']" aria-hidden="true" />
											Edit
										</b-button>
										<span
											v-b-tooltip.hover="!data.item.is_active ? { variant: 'warning' } : { variant: 'info' }"
											:title="data.item.is_active ? 'Run commission for a period using this setup' : 'Activate this setup before you can run it'"
										>
											<b-button
												class="akkurate-green-btn btn-size text-white"
												:disabled="!data.item.is_active"
												aria-label="Run commission"
												@click="openRunModal(data.item)"
											>
												<font-awesome-icon class="me-1" :icon="['fas', 'circle-play']" aria-hidden="true" />
												Run
											</b-button>
										</span>
										<b-button
											class="btn-size"
											variant="outline-info"
											v-b-tooltip.hover="{ variant: 'info' }"
											title="View past runs for this setup"
											aria-label="Run history"
											@click="openHistoryModal(data.item)"
										>
											<font-awesome-icon class="me-1" :icon="['fas', 'history']" aria-hidden="true" />
											History
										</b-button>
										<b-button
											class="btn-size"
											variant="outline-danger"
											v-b-tooltip.hover="{ variant: 'info' }"
											title="Permanently delete this setup"
											aria-label="Delete setup"
											@click="confirmDelete(data.item)"
										>
											<font-awesome-icon class="me-1" :icon="['fas', 'trash']" aria-hidden="true" />
											Delete
										</b-button>
									</div>
								</template>
							</b-table>

							<div class="d-flex justify-content-between align-items-center mt-2 px-1 flex-wrap gap-2">
								<b-pagination
									v-model="currentPage"
									:total-rows="totalRecords"
									:per-page="perPage"
									@change="onPageChange"
									class="mb-0"
								/>
								<div class="small akkurate-dark">
									{{ currentPage }} / {{ Math.max(1, Math.ceil(totalRecords / perPage)) }} — {{ totalRecords }} total
								</div>
								<b-form-group label="Per page" class="mb-0">
									<b-form-select
										v-model="perPage"
										class="field-container"
										:options="perPageOptions"
										@change="loadSetups"
									/>
								</b-form-group>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>

		<!-- Create / Edit -->
		<b-modal
			v-model="showFormModal"
			size="lg"
			:title="editingId ? 'Edit commission setup' : 'Create commission setup'"
			@hidden="resetForm"
		>
			<b-overlay :show="formLoading" rounded="sm">
				<div class="row g-2">
					<div class="col-md-6">
						<label class="akkurate-dark small">Name <span class="text-danger">*</span></label>
						<b-form-input v-model="form.name" class="field-container" required />
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Code</label>
						<b-form-input v-model="form.code" class="field-container" />
					</div>
					<div class="col-12">
						<label class="akkurate-dark small">Description</label>
						<b-form-textarea v-model="form.description" rows="2" class="field-container" />
					</div>
					<div class="col-md-6">
						<b-form-checkbox v-model="form.is_active"> Active </b-form-checkbox>
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Charge product (account type) <span class="text-danger">*</span></label>
						<multiselect
							v-model="form.charge_account_type"
							:options="accountTypes"
							label="name"
							track-by="id"
							placeholder="Select product"
							class="field-container"
						/>
					</div>
					<div class="col-md-6">
						<label class="akkurate-dark small">Commission method <span class="text-danger">*</span></label>
						<b-form-select
							v-model="form.commission_method"
							:options="methodOptions"
							class="field-container"
						/>
					</div>
					<div v-if="form.commission_method === 'flat_amount'" class="col-md-6">
						<label class="akkurate-dark small">Flat amount <span class="text-danger">*</span></label>
						<b-form-input
							v-model.number="form.commission_flat_amount"
							type="number"
							step="any"
							min="0"
							class="field-container"
						/>
					</div>
					<div v-if="form.commission_method === 'percent_of_credits'" class="col-md-6">
						<label class="akkurate-dark small">Percent (0–100) <span class="text-danger">*</span></label>
						<b-form-input
							v-model.number="form.commission_percent"
							type="number"
							step="any"
							min="0"
							max="100"
							class="field-container"
						/>
					</div>
					<div v-if="form.commission_method === 'tiered_sum_of_credits'" class="col-12">
						<label class="akkurate-dark small">
							Credit tiers (GHS brackets on sum of deposits) <span class="text-danger">*</span>
						</label>
						<p class="small text-muted mb-2">
							Fixed fee per bracket from the <strong>sum of completed Deposit</strong> transactions on
							<strong>eligibility products</strong> in the run period (inclusive bounds; leave
							<strong>Max</strong> empty on the last row for “and above”).
							Example: 20–199 → 2; 200–500 → 5; 501–999 → 10; 1000+ → 20 (last row: min 1000, no max).
						</p>
						<div class="table-responsive border rounded">
							<table class="table table-sm table-bordered mb-0 small">
								<thead class="table-light">
									<tr>
										<th>Min</th>
										<th>Max <span class="text-muted fw-normal">(optional)</span></th>
										<th>Fee (GHS)</th>
										<th style="width: 3rem"></th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(tier, idx) in form.commission_credit_tiers" :key="'tier-' + idx">
										<td>
											<b-form-input
												v-model="tier.min"
												type="number"
												step="any"
												min="0"
												class="field-container"
												placeholder="Min"
											/>
										</td>
										<td>
											<b-form-input
												v-model="tier.max"
												type="number"
												step="any"
												min="0"
												class="field-container"
												placeholder="Open-ended if empty"
											/>
										</td>
										<td>
											<b-form-input
												v-model="tier.amount"
												type="number"
												step="any"
												min="0"
												class="field-container"
												placeholder="Fee"
											/>
										</td>
										<td class="text-center align-middle">
											<b-button
												variant="outline-danger"
												class="btn-size px-2"
												type="button"
												:disabled="form.commission_credit_tiers.length <= 1"
												aria-label="Remove tier"
												@click="removeTierRow(idx)"
											>
												<font-awesome-icon :icon="['fas', 'trash']" aria-hidden="true" />
											</b-button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<b-button
							variant="outline-secondary"
							class="btn-size mt-2"
							type="button"
							@click="addTierRow"
						>
							<font-awesome-icon class="me-1" :icon="['fas', 'plus']" aria-hidden="true" />
							Add tier
						</b-button>
					</div>
					<div class="col-12">
						<label class="akkurate-dark small">Credit COA (subhead) <span class="text-danger">*</span></label>
						<multiselect
							v-model="form.commission_credit_coa"
							:options="coaSubheads"
							label="name"
							track-by="id"
							:custom-label="coaLabel"
							placeholder="Select COA to credit"
							class="field-container"
						/>
					</div>
					<div class="col-12">
						<label class="akkurate-dark small">Eligibility products (deposits) <span class="text-danger">*</span></label>
						<multiselect
							v-model="form.eligibility_account_types"
							:options="accountTypes"
							label="name"
							track-by="id"
							:multiple="true"
							placeholder="Select one or more products"
							class="field-container"
						/>
					</div>
					<div v-if="formError" class="col-12">
						<div class="text-danger small">{{ formError }}</div>
					</div>
				</div>
			</b-overlay>
			<template #modal-footer="{ cancel }">
				<b-button
					class="btn-size"
					variant="secondary"
					v-b-tooltip.hover="{ variant: 'info' }"
					title="Close without saving changes"
					aria-label="Cancel"
					@click="cancel()"
				>
					Cancel
				</b-button>
				<b-button
					class="akkurate-green-btn btn-size text-white"
					:disabled="formLoading"
					v-b-tooltip.hover="{ variant: 'info' }"
					title="Save this commission setup"
					aria-label="Save setup"
					@click="saveSetup"
				>
					Save
				</b-button>
			</template>
		</b-modal>

		<!-- Run -->
		<b-modal v-model="showRunModal" title="Run commission for period">
			<p class="small">
				<strong>{{ runContext.name }}</strong> — debits members and posts GL for the selected period (eligibility uses deposit sums in range).
			</p>
			<div class="row g-2">
				<div class="col-md-6">
					<label class="akkurate-dark small">Period start <span class="text-danger">*</span></label>
					<b-form-input v-model="runForm.period_start" type="date" class="field-container" />
				</div>
				<div class="col-md-6">
					<label class="akkurate-dark small">Period end <span class="text-danger">*</span></label>
					<b-form-input v-model="runForm.period_end" type="date" class="field-container" />
				</div>
				<div class="col-12">
					<label class="akkurate-dark small">Branches (optional — empty = all)</label>
					<multiselect
						v-model="runForm.branches"
						:options="branches"
						label="name"
						track-by="id"
						:multiple="true"
						placeholder="All branches"
						class="field-container"
					/>
				</div>
			</div>
			<div v-if="runResult.message" class="mt-3 small border rounded p-2 bg-light">
				<div><strong>Processed:</strong> {{ runResult.processed_count }}</div>
				<div><strong>Skipped:</strong> {{ runResult.skipped_count }}</div>
				<div><strong>Failed:</strong> {{ runResult.failed_count }}</div>
				<div><strong>Total amount:</strong> {{ runResult.total_amount }}</div>
				<div class="mt-1 text-break"><strong>Message:</strong> {{ runResult.message }}</div>
			</div>
			<template #modal-footer>
				<b-button
					class="btn-size"
					variant="secondary"
					v-b-tooltip.hover="{ variant: 'info' }"
					title="Close without running"
					aria-label="Close"
					@click="showRunModal = false"
				>
					Close
				</b-button>
				<b-button
					class="akkurate-green-btn btn-size text-white"
					v-b-tooltip.hover="{ variant: 'info' }"
					title="Confirm and run commission for the selected period"
					aria-label="Run commission"
					@click="submitRun"
				>
					Run
				</b-button>
			</template>
		</b-modal>

		<!-- History: list runs for this setup -->
		<b-modal
			v-model="showHistoryModal"
			size="xl"
			:title="'Run history — ' + (runContext.name || 'Setup')"
			@hidden="onHistoryModalHidden"
		>
			<p class="small text-muted mb-2">
				Commission setup runs (not the legacy account-level <code>commission/run</code> job). Posted counts are
				successful commission withdrawals for that run. Use <strong>View deductions</strong> for lines or
				<strong>Export Excel</strong> for Successful / Failed / Summary sheets.
			</p>
			<b-table
				small
				striped
				responsive
				:items="historyRows"
				:fields="historyFields"
				show-empty
				:busy="historyBusy"
				class="table_text_size"
			>
				<template #table-busy>
					<div class="text-center my-2"><b-spinner small /></div>
				</template>
				<template #cell(period)="data">
					{{ formatRunCellDate(data.item.period_start) }}
					<span class="text-muted">→</span>
					{{ formatRunCellDate(data.item.period_end) }}
				</template>
				<template #cell(run_progress)="data">
					<div class="run-progress-cell" style="max-width: 200px">
						<div
							v-if="data.item.total_accounts != null"
							class="small text-muted mb-1"
						>
							{{ formatAccountsScannedLabel(data.item) }}
						</div>
						<b-progress
							v-if="data.item.progress_percent != null"
							:max="100"
							height="10px"
							class="mb-1"
						>
							<b-progress-bar
								:value="safeProgressPercent(data.item.progress_percent)"
								:variant="runProgressVariant(data.item.status)"
							/>
						</b-progress>
						<div
							v-if="progressSegmentsTriple(data.item)"
							v-b-tooltip.hover="{ variant: 'dark' }"
							:title="progressSegmentsTooltip(data.item)"
							class="d-flex w-100 rounded overflow-hidden"
							style="height: 5px"
						>
							<div
								class="bg-success"
								:style="{
									width: progressSegmentsTriple(data.item).processed + '%',
								}"
							/>
							<div
								class="bg-warning"
								:style="{
									width: progressSegmentsTriple(data.item).skipped + '%',
								}"
							/>
							<div
								class="bg-danger"
								:style="{
									width: progressSegmentsTriple(data.item).failed + '%',
								}"
							/>
						</div>
						<div class="small text-muted mt-1">
							P {{ data.item.processed_count ?? "—" }} · S
							{{ data.item.skipped_count ?? "—" }} · F
							{{ data.item.failed_count ?? "—" }}
						</div>
					</div>
				</template>
				<template #cell(deductions_count)="data">
					{{ data.item.deductions_count != null ? data.item.deductions_count : "—" }}
				</template>
				<template #cell(total_amount)="data">
					{{ formatRunAmount(data.item.total_amount) }}
				</template>
				<template #cell(created_at)="data">
					{{ formatRunCellDate(data.item.created_at) }}
				</template>
				<template #cell(status)="data">
					{{ data.item.status || "—" }}
				</template>
				<template #cell(history_actions)="data">
					<div class="d-flex flex-wrap gap-1">
						<b-button
							variant="outline-primary"
							class="btn-size"
							@click="openRunDetailModal(data.item)"
						>
							<font-awesome-icon class="me-1" :icon="['fas', 'eye']" aria-hidden="true" />
							View deductions
						</b-button>
						<b-button
							variant="outline-secondary"
							class="btn-size"
							:disabled="historyExportingRunId === data.item.id"
							@click="exportRunExcel(data.item)"
						>
							<font-awesome-icon class="me-1" :icon="['fas', 'file-excel']" aria-hidden="true" />
							Export Excel
						</b-button>
					</div>
				</template>
			</b-table>
			<div
				v-if="historyRunsTotalRows > historyRunsPerPage"
				class="d-flex justify-content-end align-items-center mt-2"
			>
				<b-pagination
					v-model="historyRunsCurrentPage"
					:total-rows="historyRunsTotalRows"
					:per-page="historyRunsPerPage"
					class="mb-0"
					@change="onHistoryRunsPageChange"
				/>
			</div>
			<template #modal-footer>
				<b-button
					variant="primary"
					class="akkurate-green-btn btn-size text-white"
					v-b-tooltip.hover="{ variant: 'info' }"
					title="Close run history"
					aria-label="Close"
					@click="showHistoryModal = false"
				>
					Close
				</b-button>
			</template>
		</b-modal>

		<!-- Run detail: summary + posted deductions (paginated) -->
		<b-modal
			v-model="showRunDetailModal"
			size="xl"
			:title="runDetailModalTitle"
			@hidden="onRunDetailModalHidden"
		>
			<b-overlay :show="runDetailLoading" rounded="sm">
				<div v-if="runDetailSummary" class="small border rounded p-3 mb-3 bg-light">
					<div class="row g-2">
						<div class="col-md-4">
							<strong>Period</strong><br />
							{{ formatRunCellDate(runDetailSummary.period_start) }}
							→
							{{ formatRunCellDate(runDetailSummary.period_end) }}
						</div>
						<div class="col-md-2">
							<strong>Posted</strong><br />
							{{ runDetailSummary.deductions_count != null ? runDetailSummary.deductions_count : "—" }}
						</div>
						<div class="col-md-2">
							<strong>Processed</strong><br />
							{{ runDetailSummary.processed_count != null ? runDetailSummary.processed_count : "—" }}
						</div>
						<div class="col-md-2">
							<strong>Skipped</strong><br />
							{{ runDetailSummary.skipped_count != null ? runDetailSummary.skipped_count : "—" }}
						</div>
						<div class="col-md-2">
							<strong>Failed</strong><br />
							{{ runDetailSummary.failed_count != null ? runDetailSummary.failed_count : "—" }}
						</div>
						<div class="col-md-4 mt-2">
							<strong>Total amount</strong><br />
							{{ formatRunAmount(runDetailSummary.total_amount) }}
						</div>
						<div class="col-md-4 mt-2" v-if="runDetailSummary.total_accounts != null">
							<strong>Accounts</strong><br />
							{{ formatAccountsScannedLabel(runDetailSummary) }}
						</div>
						<div class="col-md-4 mt-2" v-if="runDetailSummary.progress_percent != null">
							<strong>Progress</strong><br />
							<b-progress :max="100" height="10px" class="mt-1" style="max-width: 12rem">
								<b-progress-bar
									:value="safeProgressPercent(runDetailSummary.progress_percent)"
									:variant="runProgressVariant(runDetailSummary.status)"
								/>
							</b-progress>
							<span class="small text-muted">{{ safeProgressPercent(runDetailSummary.progress_percent) }}%</span>
						</div>
						<div class="col-12 mt-2" v-if="runDetailSummary.message">
							<strong>Message</strong>
							<div class="text-break">{{ runDetailSummary.message }}</div>
						</div>
					</div>
				</div>

				<h6 class="fw-bold text-green small mb-2">Posted deductions</h6>
				<p class="small text-muted mb-2">
					Each row is a posted commission charge (withdrawal) for a member account. Use the link to open the transaction on the COA ledger view.
				</p>
				<b-table
					small
					striped
					responsive
					:items="deductionsRows"
					:fields="deductionFields"
					show-empty
					:busy="deductionsBusy"
					class="table_text_size"
				>
					<template #table-busy>
						<div class="text-center my-2"><b-spinner small /></div>
					</template>
					<template #cell(amount)="d">
						{{ displayNumber(d.item.amount) }}
					</template>
					<template #cell(period)="d">
						{{ formatRunCellDate(d.item.period_start) }} → {{ formatRunCellDate(d.item.period_end) }}
					</template>
					<template #cell(transaction_link)="d">
						<b-link
							v-if="d.item.transaction_id"
							:href="transactionLedgerHref(d.item.transaction_id)"
							target="_blank"
							rel="noopener"
							class="small"
						>
							{{ d.item.trans_code || "TRA" + d.item.transaction_id }}
						</b-link>
						<span v-else class="text-muted">—</span>
					</template>
				</b-table>
				<div
					v-if="deductionsTotalRows > deductionsPerPage"
					class="d-flex justify-content-end align-items-center mt-2 flex-wrap gap-2"
				>
					<b-pagination
						v-model="deductionsCurrentPage"
						:total-rows="deductionsTotalRows"
						:per-page="deductionsPerPage"
						size="sm"
						class="mb-0"
						@change="onDeductionsPageChange"
					/>
				</div>
			</b-overlay>
			<template #modal-footer>
				<b-button
					variant="outline-secondary"
					class="btn-size"
					:disabled="historyExportingRunId === historyRunContext.runId"
					@click="exportCurrentRunExcel"
				>
					<font-awesome-icon class="me-1" :icon="['fas', 'file-excel']" aria-hidden="true" />
					Export Excel
				</b-button>
				<b-button
					variant="secondary"
					class="btn-size"
					@click="showRunDetailModal = false"
				>
					Back
				</b-button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ApiService from "@/core/services/api.service";
import config from "@/config.js";
import Multiselect from "vue-multiselect";
import ChatBot from "@/views/components/AI/ChatBot.vue";

export default {
	name: "CommissionSetup",
	components: {
		SidebarView,
		TopbarView,
		Multiselect,
		ChatBot,
	},
	data() {
		return {
			isBusy: false,
			rows: [],
			totalRecords: 0,
			currentPage: 1,
			perPage: 10,
			perPageOptions: [10, 20, 50, 100],
			filters: {
				filter: "",
				is_active: "",
			},
			activeOptions: [
				{ value: "", text: "All" },
				{ value: true, text: "Active" },
				{ value: false, text: "Inactive" },
			],
			methodOptions: [
				{ value: "flat_amount", text: "Flat amount" },
				{ value: "percent_of_credits", text: "% of credits" },
				{ value: "tiered_sum_of_credits", text: "Tiered (sum of deposits)" },
			],
			tableFields: [
				{ key: "name", label: "Name" },
				{ key: "code", label: "Code" },
				{ key: "commission_method", label: "Method" },
				{ key: "is_active", label: "Active" },
				{ key: "actions", label: "Actions" },
			],
			accountTypes: [],
			coaSubheads: [],
			branches: [],
			showFormModal: false,
			formLoading: false,
			editingId: null,
			form: this.blankForm(),
			formError: "",
			showRunModal: false,
			runContext: {},
			runForm: {
				period_start: "",
				period_end: "",
				branches: [],
			},
			runResult: {
				processed_count: null,
				skipped_count: null,
				failed_count: null,
				total_amount: null,
				message: "",
			},
			showHistoryModal: false,
			historyBusy: false,
			historyRows: [],
			historyFields: [
				{ key: "id", label: "Run" },
				{ key: "period", label: "Period" },
				{ key: "status", label: "Status" },
				{ key: "run_progress", label: "Progress" },
				{ key: "deductions_count", label: "Posted" },
				{ key: "total_amount", label: "Total" },
				{ key: "created_at", label: "Started" },
				{ key: "history_actions", label: "Actions" },
			],
			historyRunsCurrentPage: 1,
			historyRunsPerPage: 15,
			historyRunsTotalRows: 0,
			historyExportingRunId: null,
			deductionFields: [
				{ key: "account_number", label: "Account" },
				{ key: "amount", label: "Amount (GHS)" },
				{ key: "period", label: "Period" },
				{ key: "trans_narration", label: "Narration" },
				{ key: "trans_date", label: "Trans date" },
				{ key: "transaction_link", label: "Transaction" },
			],
			showRunDetailModal: false,
			runDetailLoading: false,
			runDetailSummary: null,
			historyRunContext: {
				setupId: null,
				runId: null,
			},
			deductionsRows: [],
			deductionsBusy: false,
			deductionsCurrentPage: 1,
			deductionsTotalRows: 0,
			deductionsPerPage: 25,
		};
	},
	computed: {
		runDetailModalTitle() {
			const id = this.historyRunContext?.runId;
			return id ? `Run #${id} — posted deductions` : "Run details";
		},
	},
	mounted() {
		this.loadDropdowns();
		this.loadSetups();
	},
	methods: {
		blankForm() {
			return {
				name: "",
				code: "",
				description: "",
				is_active: true,
				charge_account_type: null,
				commission_method: "flat_amount",
				commission_flat_amount: null,
				commission_percent: null,
				/** UI rows → API `commission_credit_tiers`: [{ min, max?, amount }] */
				commission_credit_tiers: [{ min: "", max: "", amount: "" }],
				commission_credit_coa: null,
				eligibility_account_types: [],
			};
		},
		coaLabel({ name, code }) {
			return `${code || ""} ${name || ""}`.trim();
		},
		formatMethod(m) {
			if (m === "flat_amount") return "Flat amount";
			if (m === "percent_of_credits") return "% of credits";
			if (m === "tiered_sum_of_credits") return "Tiered (deposits)";
			return m || "—";
		},
		normalizeTiersFromApi(raw) {
			let data = raw;
			if (typeof data === "string") {
				try {
					data = JSON.parse(data);
				} catch (e) {
					data = [];
				}
			}
			if (!data || !Array.isArray(data) || data.length === 0) {
				return [{ min: "", max: "", amount: "" }];
			}
			return data.map((t) => ({
				min: t.min != null && t.min !== "" ? String(t.min) : "",
				max: t.max != null && t.max !== "" ? String(t.max) : "",
				amount: t.amount != null && t.amount !== "" ? String(t.amount) : "",
			}));
		},
		buildTiersPayload() {
			const rows = this.form.commission_credit_tiers || [];
			return rows
				.map((r) => {
					const min = r.min === "" || r.min == null ? NaN : Number(r.min);
					const amount = r.amount === "" || r.amount == null ? NaN : Number(r.amount);
					if (Number.isNaN(min) || Number.isNaN(amount)) return null;
					const out = { min, amount };
					const maxRaw = r.max;
					if (maxRaw !== "" && maxRaw != null && !Number.isNaN(Number(maxRaw))) {
						out.max = Number(maxRaw);
					}
					return out;
				})
				.filter(Boolean);
		},
		addTierRow() {
			this.form.commission_credit_tiers.push({ min: "", max: "", amount: "" });
		},
		removeTierRow(idx) {
			if (this.form.commission_credit_tiers.length <= 1) return;
			this.form.commission_credit_tiers.splice(idx, 1);
		},
		async loadDropdowns() {
			try {
				const [commissionDd, acc, jr] = await Promise.all([
					ApiService.get("/commission-setups/dropdown").catch(() => ({ data: {} })),
					ApiService.get("/accounts/create/dropdown"),
					ApiService.get("/journals/dropdown"),
				]);
				const cd = commissionDd.data?.data ?? commissionDd.data ?? {};
				this.accountTypes =
					cd.account_types?.length ? cd.account_types : acc.data?.account_types || [];
				this.branches =
					cd.branches?.length ? cd.branches : acc.data?.branches || [];
				this.coaSubheads =
					cd.coa_subheads?.length ? cd.coa_subheads : jr.data?.coa_subheads || [];
			} catch (e) {
				/* non-blocking */
			}
		},
		async loadSetups() {
			this.isBusy = true;
			this.$Progress.start();
			try {
				const res = await ApiService.post("/commission-setups/paginate", {
					page: this.currentPage,
					per_page: this.perPage,
					filter: this.filters.filter || undefined,
					is_active:
						this.filters.is_active === ""
							? undefined
							: this.filters.is_active,
				});
				const body = res.data;
				this.rows = body.data || body || [];
				this.totalRecords =
					body.meta?.total ??
					body.total ??
					(Array.isArray(this.rows) ? this.rows.length : 0);
			} catch (e) {
				toast.fire({
					icon: "error",
					title: e.response?.data?.message || "Failed to load setups",
				});
			} finally {
				this.isBusy = false;
				this.$Progress.finish();
			}
		},
		onPageChange(p) {
			this.currentPage = p;
			this.loadSetups();
		},
		openCreateModal() {
			this.editingId = null;
			this.form = this.blankForm();
			this.formError = "";
			this.showFormModal = true;
		},
		async openEditModal(row) {
			this.formError = "";
			this.formLoading = true;
			this.editingId = row.id;
			this.showFormModal = true;
			try {
				const res = await ApiService.get(`/commission-setups/show/${row.id}`);
				const s = res.data?.data ?? res.data;
				this.form = {
					name: s.name ?? "",
					code: s.code ?? "",
					description: s.description ?? "",
					is_active: !!s.is_active,
					charge_account_type:
						this.accountTypes.find((x) => x.id === s.charge_account_type_id) ||
						s.charge_account_type ||
						null,
					commission_method:
						s.commission_method || "flat_amount",
					commission_flat_amount: s.commission_flat_amount ?? null,
					commission_percent: s.commission_percent ?? null,
					commission_credit_tiers: this.normalizeTiersFromApi(
						s.commission_credit_tiers
					),
					commission_credit_coa:
						this.coaSubheads.find((x) => x.id === (s.commission_credit_coa_id || s.commission_credit_coa?.id)) ||
						s.commission_credit_coa ||
						null,
					eligibility_account_types: this.mapEligibility(s),
				};
			} catch (e) {
				toast.fire({
					icon: "error",
					title: e.response?.data?.message || "Failed to load setup",
				});
				this.showFormModal = false;
			} finally {
				this.formLoading = false;
			}
		},
		mapEligibility(s) {
			const ids =
				s.eligibility_account_type_ids ||
				s.eligibility_account_types?.map((x) => x.id) ||
				[];
			return this.accountTypes.filter((t) => ids.includes(t.id));
		},
		validateForm() {
			if (!this.form.name?.trim()) return "Name is required.";
			if (!this.form.charge_account_type?.id) return "Charge product is required.";
			if (!this.form.commission_credit_coa?.id) return "Credit COA is required.";
			if (!this.form.eligibility_account_types?.length)
				return "Select at least one eligibility product.";
			if (this.form.commission_method === "flat_amount") {
				if (this.form.commission_flat_amount == null || this.form.commission_flat_amount < 0)
					return "Enter a valid flat amount.";
			} else if (this.form.commission_method === "percent_of_credits") {
				const p = Number(this.form.commission_percent);
				if (Number.isNaN(p) || p < 0 || p > 100)
					return "Percent must be between 0 and 100.";
			} else if (this.form.commission_method === "tiered_sum_of_credits") {
				const tiers = this.buildTiersPayload();
				if (tiers.length === 0)
					return "Add at least one tier with min and fee (GHS).";
				for (let i = 0; i < tiers.length; i++) {
					const t = tiers[i];
					if (t.min < 0) return "Each tier min must be zero or greater.";
					if (t.amount < 0) return "Each tier fee must be zero or greater.";
					if (t.max != null && t.max < t.min)
						return "Tier max must be greater than or equal to min when set.";
				}
			}
			return "";
		},
		async saveSetup() {
			this.formError = this.validateForm();
			if (this.formError) return;
			const payload = {
				name: this.form.name,
				code: this.form.code || null,
				description: this.form.description || null,
				is_active: this.form.is_active,
				charge_account_type_id: this.form.charge_account_type.id,
				commission_method: this.form.commission_method,
				commission_flat_amount:
					this.form.commission_method === "flat_amount"
						? this.form.commission_flat_amount
						: null,
				commission_percent:
					this.form.commission_method === "percent_of_credits"
						? this.form.commission_percent
						: null,
				commission_credit_tiers:
					this.form.commission_method === "tiered_sum_of_credits"
						? this.buildTiersPayload()
						: null,
				commission_credit_coa_id: this.form.commission_credit_coa.id,
				eligibility_account_type_ids: this.form.eligibility_account_types.map(
					(x) => x.id
				),
			};
			this.formLoading = true;
			this.$Progress.start();
			try {
				if (this.editingId) {
					await ApiService.put(
						`/commission-setups/update/${this.editingId}`,
						payload
					);
				} else {
					await ApiService.post("/commission-setups/store", payload);
				}
				toast.fire({ icon: "success", title: "Saved successfully." });
				this.showFormModal = false;
				await this.loadSetups();
			} catch (e) {
				const msg =
					e.response?.data?.message ||
					(e.response?.data?.errors &&
						Object.values(e.response.data.errors).flat().join(" ")) ||
					"Save failed";
				toast.fire({ icon: "error", title: msg });
			} finally {
				this.formLoading = false;
				this.$Progress.finish();
			}
		},
		resetForm() {
			this.form = this.blankForm();
			this.editingId = null;
			this.formError = "";
		},
		confirmDelete(row) {
			swal.fire({
				title: "Delete setup?",
				text: row.name,
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: "Delete",
				cancelButtonText: "Cancel",
			}).then(async (result) => {
				if (!result.value && !result.isConfirmed) return;
				this.$Progress.start();
				try {
					await ApiService.delete(`/commission-setups/delete/${row.id}`);
					toast.fire({ icon: "success", title: "Deleted." });
					await this.loadSetups();
				} catch (e) {
					toast.fire({
						icon: "error",
						title: e.response?.data?.message || "Delete failed",
					});
				} finally {
					this.$Progress.finish();
				}
			});
		},
		openRunModal(row) {
			this.runContext = { id: row.id, name: row.name };
			this.runForm = {
				period_start: new Date().toISOString().slice(0, 10),
				period_end: new Date().toISOString().slice(0, 10),
				branches: [],
			};
			this.runResult = {
				processed_count: null,
				skipped_count: null,
				failed_count: null,
				total_amount: null,
				message: "",
			};
			this.showRunModal = true;
		},
		async submitRun(ev) {
			if (ev && ev.preventDefault) ev.preventDefault();
			if (!this.runForm.period_start || !this.runForm.period_end) {
				toast.fire({ icon: "warning", title: "Enter period start and end." });
				return;
			}
			const branchNote =
				this.runForm.branches?.length > 0
					? `Branches: ${this.runForm.branches.map((b) => b.name || b.id).join(", ")}.`
					: "All branches.";
			const confirm = await swal.fire({
				title: "Run commission?",
				html: `<p class="mb-1"><strong>${this.runContext.name || "Setup"}</strong></p>
					<p class="small mb-0">Period <strong>${this.runForm.period_start}</strong> → <strong>${this.runForm.period_end}</strong>.</p>
					<p class="small text-muted mt-2 mb-0">${branchNote} This debits members and posts GL.</p>`,
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: "Yes, run",
				cancelButtonText: "Cancel",
				focusCancel: true,
			});
			if (!confirm.value && !confirm.isConfirmed) return;

			const body = {
				period_start: this.runForm.period_start,
				period_end: this.runForm.period_end,
				branch_ids:
					this.runForm.branches?.length > 0
						? this.runForm.branches.map((b) => b.id)
						: undefined,
			};
			this.$Progress.start();
			try {
				const res = await ApiService.post(
					`/commission-setups/${this.runContext.id}/run`,
					body
				);
				const r = res.data?.run ?? res.data?.data ?? res.data;
				this.runResult = {
					processed_count: r?.processed_count ?? r?.processed ?? "—",
					skipped_count: r?.skipped_count ?? r?.skipped ?? "—",
					failed_count: r?.failed_count ?? r?.failed ?? "—",
					total_amount: r?.total_amount ?? "—",
					message: r?.message || res.data?.message || JSON.stringify(r),
				};
				toast.fire({ icon: "success", title: "Run completed." });
				await this.loadSetups();
			} catch (e) {
				toast.fire({
					icon: "error",
					title: e.response?.data?.message || "Run failed",
				});
			} finally {
				this.$Progress.finish();
			}
		},
		async openHistoryModal(row) {
			this.runContext = { id: row.id, name: row.name };
			this.historyRunsCurrentPage = 1;
			this.showHistoryModal = true;
			await this.loadHistoryRuns(1);
		},
		async loadHistoryRuns(page) {
			const setupId = this.runContext.id;
			if (!setupId) return;
			this.historyBusy = true;
			try {
				const res = await ApiService.get(`/commission-setups/${setupId}/runs`, {
					params: {
						page: page || 1,
						per_page: this.historyRunsPerPage,
					},
				});
				const body = res.data || {};
				const list = Array.isArray(body.data) ? body.data : [];
				this.historyRows = list;
				const meta = body.meta || {};
				this.historyRunsTotalRows =
					meta.total != null ? meta.total : list.length;
				this.historyRunsCurrentPage =
					meta.current_page != null ? meta.current_page : page || 1;
			} catch (e) {
				this.historyRows = [];
				toast.fire({
					icon: "error",
					title: e.response?.data?.message || "Could not load history",
				});
			} finally {
				this.historyBusy = false;
			}
		},
		onHistoryRunsPageChange(page) {
			this.loadHistoryRuns(page);
		},
		onHistoryModalHidden() {
			this.showRunDetailModal = false;
			this.resetRunDetailState();
			this.historyRunsCurrentPage = 1;
			this.historyRunsTotalRows = 0;
			this.historyRows = [];
		},
		onRunDetailModalHidden() {
			this.resetRunDetailState();
		},
		resetRunDetailState() {
			this.runDetailSummary = null;
			this.deductionsRows = [];
			this.deductionsTotalRows = 0;
			this.deductionsCurrentPage = 1;
			this.historyRunContext = { setupId: null, runId: null };
			this.runDetailLoading = false;
		},
		formatRunCellDate(val) {
			if (val == null || val === "") return "—";
			const s = String(val);
			return s.length >= 10 ? s.slice(0, 10) : s;
		},
		formatRunAmount(val) {
			if (val == null || val === "") return "—";
			return this.displayNumber(val);
		},
		formatAccountsScannedLabel(item) {
			const scanned =
				item.accounts_scanned != null ? item.accounts_scanned : 0;
			const total = item.total_accounts != null ? item.total_accounts : "—";
			return `${scanned} / ${total} accounts`;
		},
		safeProgressPercent(val) {
			const n = Number(val);
			if (Number.isNaN(n)) return 0;
			return Math.min(100, Math.max(0, n));
		},
		runProgressVariant(status) {
			const s = (status || "").toLowerCase();
			if (s === "completed") return "success";
			if (s === "failed") return "danger";
			if (s === "processing" || s === "running" || s === "pending")
				return "warning";
			return "info";
		},
		progressSegmentsTriple(item) {
			const s = item.progress_segments;
			if (!s || typeof s !== "object") return null;
			let p = Number(s.processed) || 0;
			let sk = Number(s.skipped) || 0;
			let f = Number(s.failed) || 0;
			const sum = p + sk + f;
			if (sum <= 0) return null;
			if (sum > 100.5) {
				return {
					processed: (p / sum) * 100,
					skipped: (sk / sum) * 100,
					failed: (f / sum) * 100,
				};
			}
			return { processed: p, skipped: sk, failed: f };
		},
		progressSegmentsTooltip(item) {
			const s = item.progress_segments;
			if (!s || typeof s !== "object") return "";
			return `Processed ${s.processed}% · Skipped ${s.skipped}% · Failed ${s.failed}%`;
		},
		resolveExportDownloadUrl(raw) {
			if (!raw) return "";
			if (/^https?:\/\//i.test(raw)) return raw;
			const base = (config.base_url || "").replace(/\/$/, "");
			const path = raw.startsWith("/") ? raw : `/${raw}`;
			return `${base}${path}`;
		},
		async exportRunExcel(runRow) {
			const setupId = this.runContext.id;
			const runId = runRow?.id;
			if (!setupId || !runId) return;
			this.historyExportingRunId = runId;
			try {
				const res = await ApiService.get(
					`/commission-setups/${setupId}/runs/${runId}/export-excel`
				);
				const payload = res.data?.data ?? res.data ?? {};
				const rawUrl = payload.download_url;
				if (!rawUrl) {
					toast.fire({
						icon: "error",
						title: "Export did not return a download URL.",
					});
					return;
				}
				const url = this.resolveExportDownloadUrl(rawUrl);
				window.open(url, "_blank", "noopener,noreferrer");
				toast.fire({
					icon: "success",
					title: payload.message || "Download started.",
				});
			} catch (e) {
				toast.fire({
					icon: "error",
					title: e.response?.data?.message || "Export failed.",
				});
			} finally {
				this.historyExportingRunId = null;
			}
		},
		exportCurrentRunExcel() {
			const runId = this.historyRunContext.runId;
			if (!runId) return;
			this.exportRunExcel({
				id: runId,
				...this.runDetailSummary,
			});
		},
		transactionLedgerHref(transactionId) {
			if (!transactionId) return "#";
			return `/chart-of-accounts/transactions?reference=TRA${transactionId}`;
		},
		async openRunDetailModal(runRow) {
			const setupId = this.runContext.id;
			const runId = runRow.id;
			if (!setupId || !runId) return;
			this.historyRunContext = { setupId, runId };
			this.runDetailSummary = { ...runRow };
			this.showRunDetailModal = true;
			this.deductionsCurrentPage = 1;
			this.runDetailLoading = true;
			try {
				await Promise.all([
					this.fetchRunDetailSummary(),
					this.loadRunDeductions(1),
				]);
			} finally {
				this.runDetailLoading = false;
			}
		},
		async fetchRunDetailSummary() {
			const { setupId, runId } = this.historyRunContext;
			if (!setupId || !runId) return;
			try {
				const res = await ApiService.get(
					`/commission-setups/${setupId}/runs/${runId}`
				);
				const s = res.data?.data ?? res.data;
				if (s && typeof s === "object") {
					this.runDetailSummary = { ...this.runDetailSummary, ...s };
				}
			} catch (e) {
				/* keep list row as summary */
			}
		},
		async loadRunDeductions(page) {
			const { setupId, runId } = this.historyRunContext;
			if (!setupId || !runId) return;
			this.deductionsBusy = true;
			try {
				const res = await ApiService.get(
					`/commission-setups/${setupId}/runs/${runId}/deductions`,
					{
						params: {
							page: page || 1,
							per_page: this.deductionsPerPage,
						},
					}
				);
				const body = res.data || {};
				const list = Array.isArray(body.data) ? body.data : [];
				this.deductionsRows = list.map((d) => this.normalizeDeductionRow(d));
				const meta = body.meta || {};
				this.deductionsTotalRows =
					meta.total != null ? meta.total : list.length;
				this.deductionsCurrentPage =
					meta.current_page != null ? meta.current_page : page || 1;
			} catch (e) {
				this.deductionsRows = [];
				toast.fire({
					icon: "error",
					title: e.response?.data?.message || "Could not load deductions",
				});
			} finally {
				this.deductionsBusy = false;
			}
		},
		normalizeDeductionRow(d) {
			const account = d.account || {};
			const tx = d.transaction || {};
			const transactionId = d.transaction_id ?? tx.id ?? null;
			return {
				...d,
				account_number: account.account_number ?? d.account_number ?? "—",
				amount: d.amount,
				period_start: d.period_start,
				period_end: d.period_end,
				transaction_id: transactionId,
				trans_code: tx.code || (transactionId ? `TRA${transactionId}` : ""),
				trans_narration: tx.narration ?? "",
				trans_date: tx.trans_date ?? "",
			};
		},
		onDeductionsPageChange(page) {
			this.loadRunDeductions(page);
		},
	},
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
